import React from "react";

class PositionsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isDataStale: false,
      lastUpdate: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("PositionsErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
      lastUpdate: new Date(),
    });

    // Log positions-specific errors
    if (window.gtag) {
      window.gtag("event", "exception", {
        description: `Positions Error: ${error.toString()}`,
        fatal: false,
        custom_map: {
          component: "positions",
          retry_count: this.state.retryCount,
        },
      });
    }

    // Show critical error toast for positions
    if (this.props.showToast) {
      this.props.showToast(
        "Positions data unavailable. Some trading information may be incomplete.",
        "error",
      );
    }
  }

  handleRetry = async () => {
    this.setState((prevState) => ({
      retryCount: prevState.retryCount + 1,
    }));

    try {
      // Call parent's refresh function if provided
      if (this.props.onRefreshPositions) {
        await this.props.onRefreshPositions();
      }

      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isDataStale: false,
      });
    } catch (error) {
      console.error("Error during positions retry:", error);
      // Error will be caught by this boundary again
    }
  };

  handleFallbackPolling = () => {
    // Switch to fallback polling mode
    if (this.props.onFallbackPolling) {
      this.props.onFallbackPolling();
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isDataStale: true,
    });
  };

  render() {
    if (this.state.hasError) {
      const { retryCount } = this.state;
      const maxRetries = 5; // Higher retry count for critical positions data

      return (
        <div className="positions-error-boundary">
          <div className="alert alert-danger" role="alert">
            <h5 className="alert-heading">
              <i className="fas fa-briefcase me-2"></i>
              Positions Data Error
            </h5>

            <p className="mb-2">
              Unable to load your trading positions. This may affect P&L
              calculations and order management.
            </p>

            {/* Critical warning for traders */}
            <div className="alert alert-warning alert-sm mb-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Trading Impact:</strong>
              Position values, P&L, and available funds may not be accurate.
              Please verify manually before placing orders.
            </div>

            {/* Error-specific guidance */}
            <div className="mb-3">
              {this.state.error?.message?.includes("network") ? (
                <div className="text-info">
                  <i className="fas fa-wifi me-1"></i>
                  <strong>Connection Issue:</strong> Check your internet
                  connection.
                </div>
              ) : this.state.error?.message?.includes("auth") ? (
                <div className="text-warning">
                  <i className="fas fa-lock me-1"></i>
                  <strong>Authentication Issue:</strong> Please log in again.
                </div>
              ) : (
                <div className="text-danger">
                  <i className="fas fa-server me-1"></i>
                  <strong>Server Issue:</strong> Trading server may be
                  temporarily unavailable.
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="d-flex flex-wrap gap-2 mb-3">
              {retryCount < maxRetries && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={this.handleRetry}
                >
                  <i className="fas fa-sync me-1"></i>
                  Retry ({retryCount}/{maxRetries})
                </button>
              )}

              <button
                className="btn btn-warning btn-sm"
                onClick={this.handleFallbackPolling}
              >
                <i className="fas fa-clock me-1"></i>
                Use Fallback Mode
              </button>

              <button
                className="btn btn-primary btn-sm"
                onClick={() => (window.location.href = "/positions")}
              >
                <i className="fas fa-external-link-alt me-1"></i>
                Open Positions Page
              </button>
            </div>

            {/* Manual alternatives for critical operations */}
            <details>
              <summary className="btn btn-outline-secondary btn-sm">
                <i className="fas fa-tools me-1"></i>
                Manual Trading Options
              </summary>
              <div className="mt-2">
                <div className="row g-2">
                  <div className="col-md-6">
                    <div className="card card-body bg-light">
                      <h6 className="card-title">Check Positions</h6>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() =>
                            window.open(
                              "https://kite.zerodha.com/portfolio/positions",
                              "_blank",
                            )
                          }
                        >
                          Kite Web
                        </button>
                        <button
                          className="btn btn-outline-success btn-sm"
                          onClick={() =>
                            (window.location.href = "/api/positions")
                          }
                        >
                          Raw API
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card card-body bg-light">
                      <h6 className="card-title">Emergency Actions</h6>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-outline-warning btn-sm"
                          onClick={() =>
                            alert(
                              "Please close positions manually via Kite if needed",
                            )
                          }
                        >
                          Close All
                        </button>
                        <button
                          className="btn btn-outline-info btn-sm"
                          onClick={() => window.location.reload()}
                        >
                          Reload App
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </details>

            {/* Error details */}
            <details className="mt-2">
              <summary className="btn btn-outline-secondary btn-sm">
                <i className="fas fa-info-circle me-1"></i>
                Technical Details
              </summary>
              <div className="mt-2 p-2 bg-light border rounded">
                <small>
                  <strong>Error:</strong>{" "}
                  {this.state.error && this.state.error.message}
                  <br />
                  <strong>Time:</strong>{" "}
                  {this.state.lastUpdate &&
                    this.state.lastUpdate.toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                    })}
                  <br />
                  <strong>Retry Count:</strong> {retryCount}
                  <br />
                  <strong>Component:</strong> Positions
                </small>
              </div>
            </details>
          </div>

          {/* Show a basic fallback positions summary if available */}
          {this.props.fallbackSummary && (
            <div className="alert alert-info">
              <h6>
                <i className="fas fa-info-circle me-1"></i>
                Last Known Summary (may be outdated)
              </h6>
              <div className="row">
                <div className="col-4">
                  <small>
                    Total Value: ₹{this.props.fallbackSummary.total_value || 0}
                  </small>
                </div>
                <div className="col-4">
                  <small>
                    Call Value: ₹
                    {this.props.fallbackSummary.total_call_value || 0}
                  </small>
                </div>
                <div className="col-4">
                  <small>
                    Put Value: ₹
                    {this.props.fallbackSummary.total_put_value || 0}
                  </small>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for wrapping positions components
export const withPositionsErrorBoundary = (WrappedComponent) => {
  return React.forwardRef((props, ref) => (
    <PositionsErrorBoundary
      onRefreshPositions={props.onRefreshPositions}
      onFallbackPolling={props.onFallbackPolling}
      fallbackSummary={props.fallbackSummary}
      showToast={props.showToast}
    >
      <WrappedComponent {...props} ref={ref} />
    </PositionsErrorBoundary>
  ));
};

export default PositionsErrorBoundary;
